import type { Rect, UiRects } from "../../game/input";
import { CANVAS_WIDTH } from "../../game/constants";
import type { RenderModel } from "../renderModel";
import type { drawButton } from "../ui";
import {
  GAME_FONT,
  MENU_TEXT_PRIMARY,
  MENU_TEXT_SECONDARY,
  uiCloseSize,
  UI_CLOSE_Y,
  UI_EDGE_PAD,
  menuBtnH,
  menuFullButtonW,
  menuGap,
  mFont,
  mFontPlain,
  roundedRectPath,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface ProfileSettingsDeps {
  ctx: CanvasRenderingContext2D;
  drawBtnStagger: (
    rect: Rect,
    label: string,
    index: number,
    options?: Parameters<typeof drawButton>[3],
  ) => void;
  drawCircleCloseButton: (rect: Rect) => void;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawProfileSettingsScreen(deps: ProfileSettingsDeps, model: RenderModel): UiRects {
  const { ctx, drawBtnStagger, drawCircleCloseButton } = deps;
  const centerX = CANVAS_WIDTH / 2;
  const uiRects: UiRects = {};

  ctx.fillStyle = MENU_TEXT_PRIMARY;
  ctx.font = `bold 11px ${GAME_FONT}`;
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("SETTINGS", centerX, 132);

  const rowW = menuFullButtonW();
  const rowH = 42;
  const rowX = centerX - rowW / 2;
  const psGap = menuGap();
  const leftW = Math.floor(rowW * 0.62);
  const rightW = rowW - leftW - psGap;

  const currentUsername = model.username ?? "NOT SET";
  const usernameRowY = 178;
  roundedRectPath(ctx, rowX, usernameRowY, leftW, rowH, 6);
  ctx.fillStyle = "rgba(14,24,42,0.95)";
  ctx.fill();
  roundedRectPath(ctx, rowX, usernameRowY, leftW, rowH, 6);
  ctx.strokeStyle = "#5c8cd4";
  ctx.stroke();
  ctx.fillStyle = "#8eb6d9";
  ctx.font = mFontPlain(6);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("USERNAME", rowX + 8, usernameRowY + 8);
  ctx.fillStyle = "#d4e7ff";
  ctx.font = mFont("bold", 7);
  ctx.fillText(currentUsername.toUpperCase().slice(0, 16), rowX + 8, usernameRowY + 22);

  const changeUsernameRect: Rect = { x: rowX + leftW + psGap, y: usernameRowY, w: rightW, h: rowH };
  drawBtnStagger(changeUsernameRect, "CHANGE", 0, {
    fill: "#1a2a4c",
    stroke: "#5c8cd4",
    font: mFont("bold", 6)
  });
  uiRects.changeUsername = changeUsernameRect;

  const offlineOn = model.appearOffline === true;
  const presenceRowY = usernameRowY + rowH + menuGap();
  roundedRectPath(ctx, rowX, presenceRowY, leftW, rowH, 6);
  ctx.fillStyle = "rgba(14,24,42,0.95)";
  ctx.fill();
  roundedRectPath(ctx, rowX, presenceRowY, leftW, rowH, 6);
  ctx.strokeStyle = "#5c8cd4";
  ctx.stroke();
  ctx.fillStyle = "#8eb6d9";
  ctx.font = mFontPlain(6);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText("APPEAR OFFLINE", rowX + 8, presenceRowY + 8);
  ctx.fillStyle = offlineOn ? "#ff9966" : "#4dff88";
  ctx.font = mFont("bold", 7);
  ctx.fillText(offlineOn ? "ENABLED" : "DISABLED", rowX + 8, presenceRowY + 22);

  const toggleRect: Rect = { x: rowX + leftW + psGap, y: presenceRowY, w: rightW, h: rowH };
  drawBtnStagger(toggleRect, offlineOn ? "ON" : "OFF", 1, {
    fill: "#1a2a4c",
    stroke: offlineOn ? "#ff9966" : "#4dff88",
    font: mFont("bold", 7)
  });
  uiRects.toggleAppearOffline = toggleRect;

  // Push notifications row (only if browser supports it)
  let pushBottomY = presenceRowY + rowH;
  if (model.pushSupported) {
    const pushOn = model.pushEnabled;
    const pushRowY = presenceRowY + rowH + psGap;
    roundedRectPath(ctx, rowX, pushRowY, leftW, rowH, 6);
    ctx.fillStyle = "rgba(14,24,42,0.95)";
    ctx.fill();
    roundedRectPath(ctx, rowX, pushRowY, leftW, rowH, 6);
    ctx.strokeStyle = "#5c8cd4";
    ctx.stroke();
    ctx.fillStyle = "#8eb6d9";
    ctx.font = mFontPlain(6);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("PUSH NOTIFICATIONS", rowX + 8, pushRowY + 8);
    ctx.fillStyle = pushOn ? "#4dff88" : "#ff9966";
    ctx.font = mFont("bold", 7);
    ctx.fillText(pushOn ? "ENABLED" : "DISABLED", rowX + 8, pushRowY + 22);

    const pushToggleRect: Rect = { x: rowX + leftW + psGap, y: pushRowY, w: rightW, h: rowH };
    drawBtnStagger(pushToggleRect, pushOn ? "ON" : "OFF", 2, {
      fill: "#1a2a4c",
      stroke: pushOn ? "#4dff88" : "#ff9966",
      font: mFont("bold", 7)
    });
    uiRects.togglePushNotifications = pushToggleRect;
    pushBottomY = pushRowY + rowH;
  }

  // Wallet section divider
  const dividerY = pushBottomY + psGap * 2;
  ctx.strokeStyle = "rgba(130,170,255,0.15)";
  ctx.beginPath();
  ctx.moveTo(rowX, dividerY);
  ctx.lineTo(rowX + rowW, dividerY);
  ctx.stroke();

  // Section label
  const walletLabelY = dividerY + psGap;
  ctx.fillStyle = MENU_TEXT_SECONDARY;
  ctx.font = mFontPlain(5);
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("ETH WALLET", centerX, walletLabelY);

  const walletRowY = walletLabelY + 14;
  let walletBottomY = walletRowY; // track bottom of wallet section

  if (model.walletConnecting) {
    ctx.fillStyle = "#a6b7ff";
    ctx.font = mFontPlain(6);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("CONNECTING...", centerX, walletRowY + 10);
    walletBottomY = walletRowY + 30;
  } else if (model.nftSyncing) {
    ctx.fillStyle = "#a6b7ff";
    ctx.font = mFontPlain(6);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillText("SYNCING NFTS...", centerX, walletRowY + 10);
    walletBottomY = walletRowY + 30;
  } else if (model.walletAddress != null) {
    // Connected: address display row
    roundedRectPath(ctx, rowX, walletRowY, leftW, rowH, 6);
    ctx.fillStyle = "rgba(14,24,42,0.95)";
    ctx.fill();
    roundedRectPath(ctx, rowX, walletRowY, leftW, rowH, 6);
    ctx.strokeStyle = "#7060ff";
    ctx.stroke();
    ctx.fillStyle = "#8eb6d9";
    ctx.font = mFontPlain(5);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("WALLET", rowX + 8, walletRowY + 6);
    ctx.fillStyle = "#d4e7ff";
    ctx.font = mFont("bold", 6);
    ctx.fillText(model.walletAddress, rowX + 8, walletRowY + 18);
    // Sync age
    const isStale = model.nftCacheStale || model.walletSyncStatus === "stale";
    ctx.fillStyle = isStale ? "#ffcc44" : "#4dff88";
    ctx.font = mFontPlain(4);
    ctx.fillText(model.nftSyncAge, rowX + 8, walletRowY + 30);

    // Sync button
    const syncStroke = isStale ? "#ffcc44" : "#64e4ff";
    const syncRect: Rect = { x: rowX + leftW + psGap, y: walletRowY, w: rightW, h: rowH };
    drawBtnStagger(syncRect, "SYNC", 2, { fill: "#1a2a4c", stroke: syncStroke, font: mFont("bold", 6) });
    uiRects.walletSync = syncRect;

    // Unlink button
    const unlinkRowY = walletRowY + rowH + psGap;
    const unlinkRect: Rect = { x: rowX, y: unlinkRowY, w: rowW, h: menuBtnH() };
    drawBtnStagger(unlinkRect, "UNLINK WALLET", 3, { fill: "#2a1010", stroke: "#ff6644", font: mFont("bold", 6) });
    uiRects.walletUnlink = unlinkRect;
    walletBottomY = unlinkRowY + menuBtnH();

    // Delegated vaults display — shown when delegate.xyz cold vaults are synced
    if (model.delegatedVaults.length > 0) {
      const vaultLabelY = walletBottomY + psGap;
      ctx.fillStyle = "#aaaaaa";
      ctx.font = mFontPlain(4);
      ctx.textAlign = "left";
      ctx.textBaseline = "top";
      ctx.fillText("DELEGATED VAULTS", rowX, vaultLabelY);
      const shortened = model.delegatedVaults.map((v) =>
        v.length >= 10 ? `${v.slice(0, 6)}...${v.slice(-4)}` : v
      );
      ctx.fillStyle = "#888888";
      ctx.font = mFontPlain(4);
      ctx.fillText(shortened.join(", "), rowX, vaultLabelY + 12);
      walletBottomY = vaultLabelY + 24;
    }
  } else {
    // Not connected: always show connect button
    const connectRect: Rect = { x: rowX, y: walletRowY, w: rowW, h: rowH };
    drawBtnStagger(connectRect, "CONNECT ETHEREUM", 2, { fill: "#1a2040", stroke: "#7060ff", font: mFont("bold", 6) });
    uiRects.walletConnect = connectRect;
    walletBottomY = walletRowY + rowH;
  }

  // ── Solana wallet section ──────────────────────────────────────────
  const solDividerY = walletBottomY + psGap * 2;
  ctx.strokeStyle = "rgba(20,241,149,0.15)";
  ctx.beginPath();
  ctx.moveTo(rowX, solDividerY);
  ctx.lineTo(rowX + rowW, solDividerY);
  ctx.stroke();

  const solLabelY = solDividerY + psGap;
  ctx.fillStyle = MENU_TEXT_SECONDARY;
  ctx.font = mFontPlain(5);
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText("SOLANA WALLET", centerX, solLabelY);

  const solRowY = solLabelY + 14;

  if (model.solWalletConnected && model.solWalletAddress != null) {
    // Connected: address display row
    roundedRectPath(ctx, rowX, solRowY, leftW, rowH, 6);
    ctx.fillStyle = "rgba(10,28,20,0.95)";
    ctx.fill();
    roundedRectPath(ctx, rowX, solRowY, leftW, rowH, 6);
    ctx.strokeStyle = "#14f195";
    ctx.stroke();
    ctx.fillStyle = "#8eb6d9";
    ctx.font = mFontPlain(5);
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillText("SOL WALLET", rowX + 8, solRowY + 6);
    ctx.fillStyle = "#d4e7ff";
    ctx.font = mFont("bold", 6);
    ctx.fillText(model.solWalletAddress, rowX + 8, solRowY + 18);

    // Disconnect button
    const solDisconnectRect: Rect = { x: rowX + leftW + psGap, y: solRowY, w: rightW, h: rowH };
    drawBtnStagger(solDisconnectRect, "UNLINK", 2, { fill: "#2a1010", stroke: "#ff6644", font: mFont("bold", 6) });
    uiRects.solWalletDisconnect = solDisconnectRect;
  } else {
    // Not connected: show connect button
    const solConnectRect: Rect = { x: rowX, y: solRowY, w: rowW, h: rowH };
    drawBtnStagger(solConnectRect, "CONNECT SOLANA", 2, { fill: "#0a1c14", stroke: "#14f195", font: mFont("bold", 6) });
    uiRects.solWalletConnect = solConnectRect;
  }

  // Back button — positioned relative to content
  const backRect: Rect = { x: CANVAS_WIDTH - uiCloseSize() - UI_EDGE_PAD, y: UI_CLOSE_Y, w: uiCloseSize(), h: uiCloseSize() };
  drawCircleCloseButton(backRect);
  uiRects.profileSettingsBack = backRect;

  return uiRects;
}
